import {
    AttributeDefinitionType,
    InputType,
    NotificationPropsType,
    TaskClasses,
    TaskAttribute,
    AttributesData,
    UpdatedAttributesData,
} from 'Types/Tasks.types'
import { AttributeContext } from '../AttributeManager'
import {
    AttributeGroupConfigExtended,
    AttributeGroupEvent,
    BaseAttributeGroup,
} from './BaseAttributeGroup'
import { AttributeValueType } from 'Types/Attribute.types'
import { createReactElement } from 'shared/utils/react.utils'
import { ClassAnnotation, ClassLevelTaskProps } from 'Types/Classes.types'
import { ClassLevelAttributesList } from 'Components/tasks/Classes/ClassLevelAttributesList'
import { convertFlatToWrappedAttributes, showAttribute } from 'Utils/attributes.utils'
import { FormInstance } from 'antd/lib'

export class ClassLevelAttributeGroup extends BaseAttributeGroup<ClassLevelTaskProps> {
    attributeDefinitionType = AttributeDefinitionType.CLASS_LEVEL as const
    private forms: Map<string, FormInstance> = new Map()
    private highlightIncomplete = false
    private annotationById: Map<string, ClassAnnotation> = new Map()
    private taskClassByClassId: Map<string, TaskClasses> = new Map()
    private classAttributesCache: Map<string, TaskAttribute[]> = new Map()
    private mandatoryAttrsCache: Map<string, TaskAttribute[]> = new Map()
    private valuesForAnnotationCache: Map<string, AttributesData | undefined> = new Map()
    private attributeByName: Map<string, TaskAttribute> = new Map()

    constructor(
        attributeContext: AttributeContext,
        config: AttributeGroupConfigExtended<Record<string, unknown>>
    ) {
        super(attributeContext, config as AttributeGroupConfigExtended<ClassLevelTaskProps>)
        this.build()
    }

    getAttributesForClass(classVersionId: string) {
        if (this.classAttributesCache.has(classVersionId)) {
            return this.classAttributesCache.get(classVersionId)!
        }

        const linkedAttributes = this.attributes.filter(attr => {
            const linkedIds = attr.linked_class_version_ids
            if (!linkedIds || linkedIds.length === 0) {
                return true
            }
            return linkedIds.includes(classVersionId)
        })
        const result = linkedAttributes.length > 0 ? linkedAttributes : this.attributes
        this.classAttributesCache.set(classVersionId, result)
        return result
    }

    isAnnotationIncomplete(annotationId: string) {
        const result = this.findAnnotationAndClass(annotationId)
        if (!result?.taskClass || !result?.annotation) return false

        const values = this.getValuesForAnnotation(annotationId)
        const visibleMandatory = this.getVisibleMandatoryAttributes(
            result.taskClass.version_id,
            values ?? {}
        )
        return visibleMandatory.some(attr => !result.annotation.attributeValues[attr.name])
    }

    getProgressInfo() {
        if (!this.taskProps) {
            return { remaining: 0, total: 0, percent: 100 }
        }

        const annotationsWithAttrs = this.getAnnotationsWithClassAttributes()
        if (!annotationsWithAttrs.length) {
            return { remaining: 0, total: 0, percent: 100 }
        }

        const taskClasses = this.taskProps.getTaskClasses()

        let total = 0
        let filled = 0

        const taskClassByClassId =
            this.taskClassByClassId.size > 0
                ? this.taskClassByClassId
                : new Map(taskClasses.map(tc => [tc.class_id, tc]))

        annotationsWithAttrs.forEach(annotation => {
            const taskClass = taskClassByClassId.get(annotation.classId)
            if (!taskClass) return

            const values = this.getValuesForAnnotation(annotation.id) ?? {}
            const visibleMandatory = this.getVisibleMandatoryAttributes(
                taskClass.version_id,
                values
            )

            total += visibleMandatory.length
            visibleMandatory.forEach(attr => {
                if (annotation.attributeValues[attr.name]) {
                    filled += 1
                }
            })
        })

        const remaining = Math.max(total - filled, 0)
        const percent = total === 0 ? 100 : Math.round((filled / total) * 100)
        return { remaining, total, percent }
    }

    private findAnnotationAndClass(annotationId: string) {
        if (!this.taskProps) return null
        const annotation = this.getAnnotationById(annotationId)
        if (!annotation) return null
        const taskClass = this.getTaskClassByClassId(annotation.classId)
        return { annotation, taskClass }
    }

    private getAnnotationById(annotationId: string) {
        if (!this.taskProps) return undefined
        const annotation = this.taskProps.getAnnotationList().find(a => a.id === annotationId)
        if (annotation) {
            this.annotationById.set(annotationId, annotation)
        }
        return annotation
    }

    private getTaskClassByClassId(classId: string) {
        if (!this.taskProps) return undefined
        const cached = this.taskClassByClassId.get(classId)
        if (cached) return cached
        const taskClass = this.taskProps.getTaskClasses().find(tc => tc.class_id === classId)
        if (taskClass) {
            this.taskClassByClassId.set(classId, taskClass)
        }
        return taskClass
    }

    private getAnnotationsWithClassAttributes() {
        if (!this.taskProps) return []

        const taskProps = this.taskProps
        const annotations = taskProps.getAnnotationList()

        return annotations.filter(annotation => {
            const taskClass =
                this.taskClassByClassId.get(annotation.classId) ??
                taskProps.getTaskClasses().find(tc => tc.class_id === annotation.classId)
            if (!taskClass) return false
            const classAttributes = this.getAttributesForClass(taskClass.version_id)
            return classAttributes.length > 0
        })
    }

    private updateAnnotationAttribute(
        annotationId: string,
        attrName: string,
        attrValue: AttributeValueType
    ) {
        if (!this.taskProps) return
        const annotation = this.getAnnotationById(annotationId)
        if (!annotation) return

        const attribute = this.attributeByName.get(attrName)
        if (!attribute) return

        if (attrValue === undefined) {
            delete annotation.attributeValues[attrName]
        } else {
            const inputType = attribute.input_definition.input_type
            let normalizedValue
            if (inputType === InputType.INT || inputType === InputType.INT_SCALE) {
                normalizedValue = typeof attrValue === 'string' ? Number(attrValue) : attrValue
            } else if (
                inputType === InputType.STRING_ARRAY ||
                inputType === InputType.MULTI_SELECT ||
                inputType === InputType.FILE
            ) {
                normalizedValue = attrValue
            } else {
                normalizedValue = String(attrValue)
            }

            annotation.attributeValues[attrName] = {
                value: normalizedValue,
                value_type: attribute.value_definition.value_type,
                value_id: annotation.attributeValues[attrName]?.value_id ?? crypto.randomUUID(),
            }
        }

        this.valuesForAnnotationCache.delete(annotationId)

        this.highlightIncomplete = false
        this.taskProps?.onAttributesChange?.()
        this.attributeContext.saveTask({
            onComplete: (notifications?: NotificationPropsType) =>
                this.attributeContext.studio.exitTask(notifications),
        })
        this.emit(AttributeGroupEvent.Change)
        this.render()
        this.updateAreMandatoryAttributesFilledForAll()
    }

    private getValuesForAnnotation(annotationId: string) {
        if (!this.taskProps) return undefined

        if (this.valuesForAnnotationCache.has(annotationId)) {
            return this.valuesForAnnotationCache.get(annotationId)
        }

        const annotation = this.getAnnotationById(annotationId)
        if (!annotation) return undefined

        const converted = convertFlatToWrappedAttributes(
            annotation.attributeValues as unknown as UpdatedAttributesData
        )
        this.valuesForAnnotationCache.set(annotationId, converted as unknown as AttributesData)
        return converted as unknown as AttributesData
    }

    build() {
        if (!this.attributes.length || !this.taskProps) {
            return
        }

        this.annotationById.clear()
        this.taskClassByClassId.clear()
        this.classAttributesCache.clear()
        this.mandatoryAttrsCache.clear()
        this.valuesForAnnotationCache.clear()
        this.attributeByName = new Map(this.attributes.map(attr => [attr.name, attr]))

        this.element = createReactElement(ClassLevelAttributesList, {
            getAnnotationsWithAttributes: () => this.getAnnotationsWithClassAttributes(),
            getAttributesForAnnotation: annotationId => {
                const result = this.findAnnotationAndClass(annotationId)
                if (!result?.taskClass) return []
                return this.getAttributesForClass(result.taskClass.version_id)
            },
            getClassForAnnotation: annotationId => {
                return this.findAnnotationAndClass(annotationId)?.taskClass
            },
            getTextPreview: annotationId => {
                return (
                    this.findAnnotationAndClass(
                        annotationId
                    )?.annotation.selection.range?.toString() ?? ''
                )
            },
            getValuesForAnnotation: annotationId => this.getValuesForAnnotation(annotationId),
            onUpdateAttribute: (annotationId, attrName, value) => {
                this.updateAnnotationAttribute(annotationId, attrName, value)
            },
            onHoverAnnotation: this.taskProps?.onHoverAnnotation,
            isReadOnly: () => this.taskProps?.isReadOnly() ?? true,
            setForm: (annotationId, form) => {
                this.forms.set(annotationId, form)
            },
            attributesOnUI: this.attributeContext.attributesOnUI,
            isAnnotationIncomplete: annotationId => this.isAnnotationIncomplete(annotationId),
            getHighlightIncomplete: () => this.highlightIncomplete,
        })
    }

    private getVisibleMandatoryAttributes(classVersionId: string, attributesData: AttributesData) {
        const classAttributes = this.getAttributesForClass(classVersionId)
        if (!this.mandatoryAttrsCache.has(classVersionId)) {
            this.mandatoryAttrsCache.set(
                classVersionId,
                classAttributes.filter(attr => !attr.input_definition.is_optional)
            )
        }
        const mandatoryAttrs = this.mandatoryAttrsCache.get(classVersionId)!
        const attributesOnUI: Record<string, 1> = {}
        const toUpdateRef = { current: {} }

        return mandatoryAttrs.filter(attr => {
            const isVisible = showAttribute(
                classAttributes,
                attr,
                attributesOnUI,
                attributesData,
                toUpdateRef
            )
            attributesOnUI[attr.name] = 1
            return isVisible
        })
    }

    private updateAreMandatoryAttributesFilledForAll() {
        if (!this.taskProps) {
            this.areMandatoryAttributesFilled = true
            return
        }

        const annotationsWithAttrs = this.getAnnotationsWithClassAttributes()
        if (!annotationsWithAttrs.length) {
            this.areMandatoryAttributesFilled = true
            return
        }

        const taskClasses = this.taskProps.getTaskClasses()
        const taskClassByClassId = new Map(taskClasses.map(tc => [tc.class_id, tc]))

        this.areMandatoryAttributesFilled = annotationsWithAttrs.every(annotation => {
            const taskClass = taskClassByClassId.get(annotation.classId)
            if (!taskClass) return true

            const values = this.getValuesForAnnotation(annotation.id)
            if (!values) return false

            const visibleMandatory = this.getVisibleMandatoryAttributes(
                taskClass.version_id,
                values
            )
            return !visibleMandatory.some(attr => !annotation.attributeValues[attr.name])
        })
    }

    validate = async () => {
        if (!this.attributes.length) return true

        const annotationsWithAttrs = this.getAnnotationsWithClassAttributes()
        if (!annotationsWithAttrs.length) return true

        const taskClasses = this.taskProps?.getTaskClasses() ?? []
        const taskClassByClassId = new Map(taskClasses.map(tc => [tc.class_id, tc]))
        const hasIncompleteAnnotations = annotationsWithAttrs.some(annotation => {
            const taskClass = taskClassByClassId.get(annotation.classId)
            if (!taskClass) return false
            const values = this.getValuesForAnnotation(annotation.id) ?? {}
            const visibleMandatory = this.getVisibleMandatoryAttributes(
                taskClass.version_id,
                values
            )
            return visibleMandatory.some(attr => !annotation.attributeValues[attr.name])
        })

        const addViolationAndFail = () => {
            this.highlightIncomplete = true
            this.render()
            this.attributeContext.studio.inputManager.addViolation({
                id: 'class-level-attributes',
                type: 'error',
                message:
                    'Cannot submit: please fill all required class-level attributes for each annotation',
            })
            this.attributeContext.studio.workbench.enqueueUiUpdate(ui => {
                ui.rightSidebar.changeActiveItemId('classes')
            })
            this.taskProps?.onValidationFail?.()
            return false
        }

        if (hasIncompleteAnnotations) {
            await Promise.all(
                Array.from(this.forms.values()).map(form =>
                    form.validateFields().catch(() => undefined)
                )
            )
            return addViolationAndFail()
        }

        const formValidations = await Promise.all(
            Array.from(this.forms.values()).map(form => form.validateFields().catch(() => false))
        )
        const allFormsValid = formValidations.every(result => result !== false)

        if (!allFormsValid) {
            return addViolationAndFail()
        }

        this.highlightIncomplete = false
        this.render()
        this.attributeContext.studio.inputManager.removeViolation('class-level-attributes', 'error')
        return true
    }

    removeAllAttributes() {
        if (!this.taskProps) return

        this.taskProps.getAnnotationList().forEach(annotation => {
            const keysToRemove = Object.keys(annotation.attributeValues).filter(
                key => key !== 'comment_attribute_name'
            )
            keysToRemove.forEach(key => delete annotation.attributeValues[key])
        })

        this.valuesForAnnotationCache.clear()
    }
}
